import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/themeClasses.js";
import {
  listCompactFilterTriggerClassName,
  listSortTriggerClassName,
  filterDrawerTriggerClassName,
} from "../../design/tokens.js";
import AppButton from "./AppButton.jsx";
import { Input } from "../forms/shared/FormAtoms.jsx";
import { Z_INDEX } from "./zIndex.js";
import { useFilterDrawerMode } from "./filterDrawerContext.js";
import { computeFixedPopoverPosition } from "../../utils/fixedPopoverPosition.js";

function DarkPopover({ pos, onClose, children, compact }) {
  const wrapRef = useRef(null);

  useEffect(() => {
    const onKey = e => e.key === "Escape" && onClose();
    const onClick = e => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) onClose();
    };
    const onRecalc = () => onClose();
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onClick, true);
    window.addEventListener("resize", onRecalc, { passive: true });
    window.addEventListener("scroll", onRecalc, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onClick, true);
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={wrapRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: Z_INDEX.selectPortal,
      }}
    >
      <div
        className={cn(
          "w-full rounded-xl popover-panel backdrop-blur-md transition-all flex flex-col overflow-hidden",
          compact ? "p-0.5 max-h-[min(50vh,240px)]" : "p-1 max-h-[min(60vh,360px)]",
        )}
        role="menu"
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

function MenuItem({ active, label, onClick, compact }) {
  return (
    <AppButton
      type="button"
      variant="ghost"
      onClick={onClick}
      role="option"
      aria-selected={active}
      className={cn(
        "w-full !justify-start text-left transition font-normal",
        compact
          ? "!min-h-0 !h-8 !py-1 !px-2.5 text-xs rounded-md"
          : "rounded-lg text-sm min-h-[44px] h-auto",
        active ? "menu-item-active font-medium" : "menu-item-idle",
      )}
    >
      {label}
      {active && !compact ? " •" : null}
    </AppButton>
  );
}

/**
 * Generischer Popover-Filter (wie StatusFilter / FalltypFilter in Listen).
 * Optional mit Suche für lange Listen (z. B. Mitarbeiter).
 */
export default function OptionPopoverFilter({
  value,
  onChange,
  options = [],
  labelAll = "Alle",
  allValue = "__all__",
  showAllOption = true,
  title = "Filter",
  triggerClassName = "",
  disabled = false,
  menuWidth = 240,
  compact = false,
  searchable = false,
  searchPlaceholder = "Suchen…",
  searchMinOptions = 10,
}) {
  const inFilterDrawer = useFilterDrawerMode();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const btnRef = useRef(null);
  const searchRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: menuWidth });

  const normalized = useMemo(
    () =>
      options.map(opt =>
        opt && typeof opt === "object"
          ? { value: opt.value, label: opt.label ?? String(opt.value ?? "") }
          : { value: opt, label: String(opt ?? "") },
      ),
    [options],
  );

  const isSearchable =
    searchable === true || (searchable !== false && normalized.length >= searchMinOptions);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!isSearchable || !q) return normalized;
    return normalized.filter(o => o.label.toLowerCase().includes(q));
  }, [normalized, query, isSearchable]);

  const currentLabel = useMemo(() => {
    if (showAllOption && (!value || value === allValue)) return labelAll;
    return normalized.find(o => String(o.value) === String(value))?.label ?? String(value ?? labelAll);
  }, [value, allValue, labelAll, normalized, showAllOption]);

  const closeMenu = () => {
    setOpen(false);
    setQuery("");
  };

  const openMenu = () => {
    if (disabled || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const estimate = Math.min(80 + filteredOptions.length * 44, 360);
    setPos(computeFixedPopoverPosition(r, { width: menuWidth, heightEstimate: estimate }));
    setQuery("");
    setOpen(true);
  };

  const toggleMenu = () => {
    if (disabled) return;
    if (inFilterDrawer) {
      setOpen(v => {
        if (v) setQuery("");
        return !v;
      });
      return;
    }
    openMenu();
  };

  useEffect(() => {
    if (!open || !isSearchable) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, isSearchable]);

  const pick = val => {
    onChange?.(val);
    closeMenu();
  };

  const menuInner = (
    <>
      {isSearchable ? (
        <div className="shrink-0 p-1.5 pb-1.5 border-b border-border-muted">
          <Input
            ref={searchRef}
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className={cn("w-full", compact ? "text-xs min-h-[32px]" : "text-sm min-h-[40px]")}
            aria-label={searchPlaceholder}
            onKeyDown={e => e.stopPropagation()}
          />
        </div>
      ) : null}
      <div role="listbox" aria-label={title} className="min-h-0 flex-1 overflow-auto">
        {showAllOption ? (
          <>
            <MenuItem
              compact={compact}
              active={value === allValue || !value}
              label={labelAll}
              onClick={() => pick(allValue)}
            />
            <div className={compact ? "my-0.5 h-px bg-border-muted" : "my-1 h-px bg-border-muted"} />
          </>
        ) : null}
        {filteredOptions.map(({ value: v, label }) => (
          <MenuItem
            key={v}
            compact={compact}
            active={String(value) === String(v)}
            label={label}
            onClick={() => pick(v)}
          />
        ))}
        {isSearchable && query.trim() && filteredOptions.length === 0 ? (
          <p className="px-3 py-2 text-xs text-secondary">Keine Treffer</p>
        ) : null}
      </div>
    </>
  );

  const triggerBtn = (
    <AppButton
      ref={btnRef}
      onClick={toggleMenu}
      type="button"
      variant="secondary"
      disabled={disabled}
      className={cn(
        compact ? listCompactFilterTriggerClassName : listSortTriggerClassName,
        inFilterDrawer ? filterDrawerTriggerClassName : "shrink-0",
        triggerClassName,
      )}
      aria-haspopup="listbox"
      aria-expanded={open}
      title={title}
    >
      {currentLabel}
    </AppButton>
  );

  if (inFilterDrawer) {
    return (
      <div className="w-full min-w-0">
        {triggerBtn}
        {open ? (
          <div
            className={cn(
              "mt-1.5 w-full rounded-xl popover-panel border border-default flex flex-col overflow-hidden",
              compact ? "p-0.5 max-h-[min(40dvh,240px)]" : "p-1 max-h-[min(40dvh,320px)]",
            )}
          >
            {menuInner}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {triggerBtn}

      {open ? (
        <DarkPopover pos={pos} onClose={closeMenu} compact={compact}>
          {menuInner}
        </DarkPopover>
      ) : null}
    </>
  );
}
